import { Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { ProgramacionServicio } from 'src/app/domain/model/agenda/agenda';
import {
  DiaSemana,
  WeekCalendarComponent
} from '../week-calendar/week-calendar.component';
import { HOUR_HEIGHT_DEFAULT_PX } from '../week-calendar/week-calendar.utils';
import { AgendaDesktopV2Service } from '../../services/agenda-desktop-v2.service';

export interface LeyendaCita {
  label: string;
  color: string;
}

export interface OpcionMes {
  label: string;
  value: number;
}

/**
 * DUMB COMPONENT: AgendaDesktopV2
 *
 * Layout desktop (>= 1024 px) del modal de agenda del asesor,
 * con panel lateral colapsable (Año/Mes + mini calendario +
 * leyenda) y grilla semanal compacta de 7 columnas con un solo scroll vertical.
 * No tiene lógica de negocio: recibe estado y emite eventos al contenedor.
 */
@Component({
  selector: 'app-agenda-desktop-v2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DropdownModule,
    TooltipModule,
    WeekCalendarComponent
  ],
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        min-height: 0;
      }

      .agenda-v2-panel {
        flex: 0 0 auto;
        width: 17.5rem;
        overflow: hidden;
        transition:
          width 180ms ease,
          opacity 180ms ease;
      }

      .agenda-v2-panel--cerrado {
        width: 0;
        opacity: 0;
      }

      .agenda-v2-panel-inner {
        box-sizing: border-box;
        width: 17.5rem;
        max-height: 100%;
        overflow-y: auto;
        padding-right: 0.75rem;
      }

      /* Modal v2: cabecera compacta y sin footer (la X del header cierra) */
      :host ::ng-deep .agenda-v2-dialog .p-dialog-header {
        padding: 0.5rem 1rem;
      }

      :host ::ng-deep .agenda-v2-dialog .p-dialog-footer {
        display: none;
      }

      :host ::ng-deep .agenda-v2-dialog .p-dialog-title {
        font-size: 1.05rem;
      }

      @media (prefers-reduced-motion: reduce) {
        .agenda-v2-panel {
          transition: none;
        }
      }
    `
  ],
  template: `
    <div class="flex h-full min-h-0">
      <!-- Panel lateral colapsable -->
      <aside
        id="agenda-panel-lateral"
        class="agenda-v2-panel"
        [class.agenda-v2-panel--cerrado]="!panelAbierto()"
        [attr.aria-hidden]="panelAbierto() ? null : 'true'"
        [attr.inert]="panelAbierto() ? null : ''"
        aria-label="Selector de fecha y leyenda"
      >
        <div class="agenda-v2-panel-inner">
          <!-- Selector Año/Mes -->
          <div class="flex gap-2 mb-2">
            <div class="flex-1 min-w-0">
              <span
                class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >Año</span
              >
              <p-dropdown
                [ngModel]="anio()"
                (ngModelChange)="anioChange.emit($event)"
                [options]="anios()"
                ariaLabel="Año"
                appendTo="body"
                [style]="{ width: '100%' }"
              >
              </p-dropdown>
            </div>
            <div class="flex-1 min-w-0">
              <span
                class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >Mes</span
              >
              <p-dropdown
                [ngModel]="mes()"
                (ngModelChange)="mesChange.emit($event)"
                [options]="meses()"
                optionLabel="label"
                optionValue="value"
                ariaLabel="Mes"
                appendTo="body"
                [style]="{ width: '100%' }"
              >
              </p-dropdown>
            </div>
          </div>

          <!-- Mini calendario mensual -->
          <div
            class="border dark:border-gray-600 rounded-lg p-1 mb-2"
            role="group"
            aria-label="Calendario mensual"
          >
            <div class="flex justify-between items-center mb-1">
              <button
                type="button"
                pButton
                icon="pi pi-chevron-left"
                class="p-button-text p-button-sm min-h-[32px] min-w-[32px]"
                aria-label="Mes anterior"
                (click)="mesAnterior.emit()"
              ></button>
              <span class="font-semibold text-sm"
                >{{ nombreMes() }} {{ anio() }}</span
              >
              <button
                type="button"
                pButton
                icon="pi pi-chevron-right"
                class="p-button-text p-button-sm min-h-[32px] min-w-[32px]"
                aria-label="Mes siguiente"
                (click)="mesSiguiente.emit()"
              ></button>
            </div>
            <div class="grid grid-cols-7 gap-0 text-center text-xs">
              @for (dia of diasSemanaCortos(); track dia) {
                <div
                  class="py-0.5 font-medium text-gray-600 dark:text-gray-400"
                >
                  {{ dia }}
                </div>
              }
              @for (empty of diasVaciosInicio(); track $index) {
                <div></div>
              }
              @for (dia of diasDelMes(); track dia) {
                <button
                  type="button"
                  class="h-7 flex items-center justify-center rounded cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-800"
                  [ngClass]="
                    esDiaMarcado()(dia)
                      ? 'bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-700'
                      : 'text-gray-800 dark:text-gray-200'
                  "
                  [class.font-bold]="esHoy()(dia)"
                  [attr.aria-current]="esHoy()(dia) ? 'date' : null"
                  [attr.aria-pressed]="esDiaMarcado()(dia)"
                  (click)="diaClick.emit(dia)"
                >
                  {{ dia }}
                </button>
              }
            </div>
          </div>

          <!-- Leyenda -->
          <div class="border dark:border-gray-600 rounded-lg p-2">
            <h4 class="font-semibold text-xs mb-1">Leyenda</h4>
            <ul class="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs">
              @for (tipo of leyenda(); track tipo.label) {
                <li class="flex items-center gap-1 min-w-0">
                  <span
                    class="w-3 h-3 rounded flex-shrink-0"
                    [style.background-color]="tipo.color"
                    aria-hidden="true"
                  ></span>
                  <span class="truncate" [title]="tipo.label">{{
                    tipo.label
                  }}</span>
                </li>
              }
            </ul>
          </div>
        </div>
      </aside>

      <!-- Zona de la grilla -->
      <section class="flex-1 min-w-0 flex flex-col min-h-0">
        <div class="flex flex-wrap items-center gap-1 pb-1">
          <button
            type="button"
            pButton
            [icon]="
              panelAbierto()
                ? 'pi pi-angle-double-left'
                : 'pi pi-angle-double-right'
            "
            class="p-button-text p-button-sm min-h-[36px] min-w-[36px] focus-visible:ring-2 focus-visible:ring-blue-600"
            [attr.aria-label]="
              panelAbierto() ? 'Ocultar panel lateral' : 'Mostrar panel lateral'
            "
            [attr.aria-expanded]="panelAbierto()"
            aria-controls="agenda-panel-lateral"
            [pTooltip]="
              panelAbierto() ? 'Ocultar panel lateral' : 'Mostrar panel lateral'
            "
            tooltipPosition="bottom"
            (click)="togglePanel()"
          ></button>

          <button
            type="button"
            pButton
            icon="pi pi-chevron-left"
            class="p-button-text p-button-sm min-h-[36px] min-w-[36px]"
            aria-label="Semana anterior"
            (click)="semanaAnterior.emit()"
          ></button>
          <span
            class="font-semibold text-sm text-gray-700 dark:text-gray-200 px-1"
            aria-live="polite"
            >{{ rangoSemana() }}</span
          >
          <button
            type="button"
            pButton
            icon="pi pi-chevron-right"
            class="p-button-text p-button-sm min-h-[36px] min-w-[36px]"
            aria-label="Semana siguiente"
            (click)="semanaSiguiente.emit()"
          ></button>
          <button
            type="button"
            pButton
            label="Hoy"
            class="p-button-outlined p-button-sm min-h-[36px]"
            (click)="hoy.emit()"
          ></button>

          @if (!panelAbierto()) {
            <ul
              class="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs ml-auto"
              aria-label="Leyenda"
            >
              @for (tipo of leyenda(); track tipo.label) {
                <li class="flex items-center gap-1">
                  <span
                    class="w-3 h-3 rounded"
                    [style.background-color]="tipo.color"
                    aria-hidden="true"
                  ></span>
                  <span>{{ tipo.label }}</span>
                </li>
              }
            </ul>
          }
        </div>

        <app-week-calendar
          [citas]="citas()"
          [diasSemana]="diasSemana()"
          [loading]="loading()"
          [tooltipText]="tooltipText()"
          [showNavigation]="false"
          [desktopV2]="true"
          [hourHeight]="hourHeight()"
          (citaClick)="citaClick.emit($event)"
          (celdaClick)="celdaClick.emit($event)"
        />
      </section>
    </div>
  `
})
export class AgendaDesktopV2Component {
  private readonly v2 = inject(AgendaDesktopV2Service);

  // === Inputs: panel lateral ===
  anio = input.required<number>();
  mes = input.required<number>();
  anios = input.required<number[]>();
  meses = input.required<OpcionMes[]>();
  nombreMes = input.required<string>();
  diasSemanaCortos = input.required<string[]>();
  diasVaciosInicio = input.required<number[]>();
  diasDelMes = input.required<number[]>();
  leyenda = input.required<LeyendaCita[]>();
  /** Día del mini calendario resaltado (semana mostrada); lo define el contenedor. */
  esDiaMarcado = input.required<(dia: number) => boolean>();
  esHoy = input.required<(dia: number) => boolean>();

  // === Inputs: grilla ===
  citas = input<ProgramacionServicio[]>([]);
  diasSemana = input<DiaSemana[]>([]);
  loading = input<boolean>(false);
  tooltipText = input<string>(
    'Haga clic en una celda vacía para crear una nueva cita'
  );
  hourHeight = input<number>(HOUR_HEIGHT_DEFAULT_PX);

  // === Outputs ===
  anioChange = output<number>();
  mesChange = output<number>();
  mesAnterior = output<void>();
  mesSiguiente = output<void>();
  diaClick = output<number>();
  semanaAnterior = output<void>();
  semanaSiguiente = output<void>();
  hoy = output<void>();
  citaClick = output<ProgramacionServicio>();
  celdaClick = output<{ fecha: string; hora: number; minuto: number }>();

  readonly panelAbierto = this.v2.panelAbierto;

  readonly rangoSemana = computed(() => {
    const dias = this.diasSemana();
    if (dias.length === 0) return '';
    return `${dias[0].fechaCorta} - ${dias[dias.length - 1].fechaCorta}`;
  });

  togglePanel(): void {
    this.v2.togglePanel();
  }
}
